// src/components/Separation/TerminationList.tsx (Updated with Modal)

import React, { useState, useEffect, useCallback } from 'react';
import { TerminationRecord } from '../../types/separation';
import { getTerminations } from '../../api/separation';
import TerminationModal from './terminationmodal';

interface TerminationListProps {
  onViewChecklist?: (type: string, id: string) => void;
  onCreateSettlement?: (type: string, id: string, employeeId: string) => void;
  userRole?: string;
  canTerminate?: boolean;
}

const TerminationList: React.FC<TerminationListProps> = ({ 
  onViewChecklist, 
  onCreateSettlement, 
  userRole = 'admin',
  canTerminate = true 
}) => {
  const [terminations, setTerminations] = useState<TerminationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const limit = 20;

  const loadTerminations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTerminations({ page, limit });
      setTerminations(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to load terminations:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadTerminations();
  }, [loadTerminations]);

  const getTerminationTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      VOLUNTARY: 'bg-blue-100 text-blue-800',
      INVOLUNTARY: 'bg-red-100 text-red-800',
      MUTUAL: 'bg-green-100 text-green-800',
      CONTRACT_END: 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const totalPages = Math.ceil(total / limit);

  const handleTerminate = (employeeId: string, employeeName: string) => {
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(employeeName);
    setIsTerminateModalOpen(true);
  };

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      </div>
    );
  }

  return (
    <>
      {/* Terminate Button (if user has permission) */}
      {canTerminate && (userRole === 'admin' || userRole === 'hr') && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => handleTerminate('', '')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
            Terminate Employee
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Termination Records</h3>
          <p className="text-sm text-gray-500 mt-1">Total: {total}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Termination Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Working Day</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eligible for Rehire</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {terminations.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">Employee ID: {t.employee_id}</div>
                    <div className="text-xs text-gray-500">ID: {t.id.slice(0, 8)}...</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {t.termination_date ? new Date(t.termination_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {t.last_working_day ? new Date(t.last_working_day).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTerminationTypeBadge(t.termination_type)}`}>
                      {t.termination_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {t.eligible_for_rehire ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-600">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm space-x-3">
                    <button
                      onClick={() => onViewChecklist?.('TERMINATION', t.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Checklist
                    </button>
                    <button
                      onClick={() => onCreateSettlement?.('TERMINATION', t.id, t.employee_id)}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      Settlement
                    </button>
                  </td>
                </tr>
              ))}
              {terminations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No termination records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Termination Modal */}
      <TerminationModal
        isOpen={isTerminateModalOpen}
        onClose={() => {
          setIsTerminateModalOpen(false);
          setSelectedEmployeeId('');
          setSelectedEmployeeName('');
        }}
        employeeId={selectedEmployeeId}
        employeeName={selectedEmployeeName}
        onSuccess={loadTerminations}
      />
    </>
  );
};

export default TerminationList;